// ═══════════════════════════════════════════════════════════════════════════
// DOMAINE : lien « vers un autre outil » d'une action
// ═══════════════════════════════════════════════════════════════════════════
//
// Deuxième barrière, côté client — même règle que le serveur
// (`app/services/external_url.py`), qui est la seule qui protège vraiment
// puisqu'un attaquant peut appeler l'API directement. Celle-ci couvre deux
// cas : un lien déjà en base écrit avant la validation serveur, et l'ouverture
// d'un lien depuis l'éditeur (un `javascript:…` ouvert par `window.open`
// s'exécuterait dans la session de celui qui clique).
//
// Fonctions PURES, testables sans DOM.

/** Protocoles autorisés. `javascript:`, `data:`, `file:` en sont absents. */
export const ALLOWED_PROTOCOLS = ['http:', 'https:'];

/** Longueur maximale, alignée sur `MAX_LENGTH` côté serveur. */
export const MAX_URL_LENGTH = 2048;

/**
 * URL utilisable pour un `window.open`, ou `null` si la saisie n'est pas une
 * URL http(s).
 *
 * Une saisie sans protocole (« jira.example.com/PROJ-42 ») est préfixée en
 * `https://` — c'est ce que l'utilisateur voulait dire, et ça ne peut pas
 * requalifier un `javascript:`, qui porte déjà un protocole. Les caractères de
 * contrôle sont rejetés, jamais retirés : ils ne servent qu'à masquer un
 * protocole interdit.
 */
export function safeExternalUrl(raw: string | null | undefined): string | null {
  const cleaned = (raw || '').trim();
  if (!cleaned || cleaned.length > MAX_URL_LENGTH) return null;
  // Caractères de contrôle : refusés plutôt que retirés (ils ne servent qu'à
  // masquer un protocole interdit). Parcours des codes plutôt qu'une regex :
  // une classe de caractères de contrôle déclenche `no-control-regex` (règle
  // utile ailleurs), et la boucle dit plus clairement ce qu'on refuse.
  for (const ch of cleaned) {
    const code = ch.charCodeAt(0);
    if (code < 0x20 || code === 0x7f) return null;
  }
  // Aucune espace : une URL valide encode la sienne (%20). `new URL()` en
  // tolère dans le chemin (il les encode), le serveur non — on s'aligne sur le
  // plus strict des deux pour que les deux barrières disent la même chose.
  if (/\s/.test(cleaned)) return null;
  const hasScheme = cleaned.includes('://') || cleaned.split('/', 1)[0]?.includes(':');
  const candidate = hasScheme ? cleaned : `https://${cleaned}`;
  try {
    const url = new URL(candidate);
    if (!ALLOWED_PROTOCOLS.includes(url.protocol) || !url.host) return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Vrai si la saisie est une URL http(s) exploitable. */
export function isSafeExternalUrl(raw: string | null | undefined): boolean {
  return safeExternalUrl(raw) !== null;
}
